import * as React from 'react'
import * as DateFns from 'date-fns'
import * as d3 from 'd3'
import styled from 'styled-components'
import { useDispatch } from 'redux-react-hook'
import { Form, Field } from 'react-final-form'
import { Icon } from '../../atoms/icon'
import { CloseButton } from '../../atoms/close-button'
import { StatusIcon } from '../../molecules/status-icon'
import { TotalStatusIcon } from '../../molecules/total-status-icon'
import { Color, Size } from '../../../constants/app.constant'
import history from '../../utils/history'
import useGetState from '../../../hooks/use-get-state'
import { ResistanceLog } from '../../../interfaces/resistance-log'
import { DayTrainingLog } from '../../../interfaces/day-training-log'
// import { dispAge, calcAge, getWeekCount } from './utils'
import { dispAge, calcAge } from './utils'
import { when } from '../../../utils/mixin'
import { Button, ButtonGroup } from '../../atoms/button'
import { putPatientShared } from '../../../actions/patient.action'
import { composeValidators, required } from '../../../utils/validator'
import TextFieldItem from '../../molecules/text-field/text-field'
import ShareButton from './share_button'
import * as moment from 'moment'

const { useState, useEffect, useCallback } = React
export default () => {
  const {
    state: {
      patient,
      patient: { shared },
      report: { reportLog },
      auth,
    },
  } = useGetState()
  const dispatch = useDispatch()
  const [node, setNode] = useState<SVGSVGElement>()
  const [modal, setModal] = useState<boolean>(false)
  const [localLogs, setLocalLogs] = useState<DayTrainingLog[]>([])
  const toggleModal = useCallback(() => setModal(!modal), [modal])
  let defaultWidth = 0
  const defaultHeight = 60
  const colors = [
    Color.STATUS_COLOR.STATUS_1,
    Color.STATUS_COLOR.STATUS_2,
    Color.STATUS_COLOR.STATUS_3,
    Color.STATUS_COLOR.STATUS_4,
  ]
  const siteUrl = String(process.env.REACT_APP_SITE_URL)

  const onSubmit = async (formValues: any) => {
    dispatch(
      putPatientShared({
        patientId: auth.user.idToken.payload['custom:userId'],
        jwtToken: auth.user.idToken.jwtToken,
        enable: formValues.enable,
        password: formValues.password,
      })
    )
  }
  useEffect(
    () => {
      if (reportLog && reportLog.patientDayTrainingLogs) {
        console.log(reportLog.patientDayTrainingLogs)
        setLocalLogs(
          reportLog.patientDayTrainingLogs.sort(
            (x: DayTrainingLog, y: DayTrainingLog) =>
              new Date(y.startedAt).getTime() -
              new Date(x.startedAt).getTime()
          )
        )
      }
    },
    [reportLog]
  )
  useEffect(
    () => {
      if (reportLog && reportLog.patientDayTrainingLogs) {
        defaultWidth = reportLog.length * 30
        const resistanceEvaluations = reportLog.patientDayTrainingLogs.map(
          (v: DayTrainingLog) =>
            v.patientDayTrainingResistanceLogs.map((log: ResistanceLog) =>
              Number(log.evaluation)
            )
        )
        const heartRates = reportLog.patientDayTrainingLogs.map(
          (v: DayTrainingLog) => Number(v.evaluation)
        )
        const svg = d3.select(node)
        svg.attr('width', defaultWidth).attr('height', defaultHeight)
        const groups = svg
          .selectAll('g')
          .data(resistanceEvaluations)
          .enter()
          .append('g')
          .attr('id', (d, i) => `id-${i}`)
        groups.each((d: number[], index: number) => {
          d3.select(`#id-${index}`)
            .selectAll('rect')
            .data(d)
            .enter()
            .append('rect')
            .attr('x', () => 24 * index)
            .attr('y', (localD, i) => defaultHeight - 10 * i - 10)
            .attr('height', () => 10)
            .attr('width', 16)
            .attr(
              'style',
              localD =>
                `fill: ${colors[localD - 1]};stroke: ${
                  Color.STATUS_COLOR.STATUS_BORDER
                };`
            )
        })
        const line: any = d3
          .line()
          .x((d, i) => 24 * i)
          .y((d: any, i) => defaultHeight - 10 * d - 10)
        svg
          .append('path')
          .datum(heartRates)
          .attr('d', line)
          .attr('stroke', Color.BLACK)
          .attr('fill', 'none')
      }
    },
    [node]
  )
  return (
    <Wrapper>
      {reportLog && (
        <PageContents>
          <UserDetail>
            <Icon.User size={`${Size.XLARGE}px`} color={Color.DARK_GRAY} />
            <UserName>
              {patient.username}
              さん
            </UserName>
            <Age>{dispAge(calcAge(patient.birthdayYmd))}</Age>
            <Share>
              <Button onClick={toggleModal} primary>
                共有
              </Button>
            </Share>
          </UserDetail>
          <TrainingMetaWrapper>
            <TrainingMetaItem>
              <Lablel>直近の運動開始</Lablel>
              <TrainingMetaBody>
                {localLogs.length > 0 && 
                  DateFns.format(localLogs[0].startedAt, 'MM月DD日')}
              </TrainingMetaBody>
            </TrainingMetaItem>
            {/*
            <TrainingMetaItem>
              <Lablel>今週の運動目標</Lablel>
              <TrainingMetaBody>
                あと
                <Count>3</Count>
                回
              </TrainingMetaBody>
            </TrainingMetaItem>
            */}
            <TrainingMetaItem>
              <Lablel>過去30日の総合評価</Lablel>
              <TrainingMetaBody>
                <TotalStatusIcon
                  evaluation={Number(reportLog.latestEvaluationAverage)}
                />
              </TrainingMetaBody>
            </TrainingMetaItem>
          </TrainingMetaWrapper>
          <TrainingGraphWrapper>
            <Lablel>今月の運動グラフ</Lablel>
            <GraphContents>
              <svg ref={node => setNode(node)} />
            </GraphContents>
          </TrainingGraphWrapper>
          <TrainingListWrapper>
            {localLogs &&
              localLogs.map((v: DayTrainingLog, index: number) => (
                <TrainingListItem
                  key={index}
                  onClick={() => history.push(`/report_detail/${v.startedAt}`)}
                >
                  <TrainingListLeft>
                    <TrainingListLabel>
                      <TrainingCount>{localLogs.length - index}</TrainingCount>
                      回目
                      {/* <Week>{getWeekCount(new Date(v.startedAt))}</Week> */}
                      <Week>{v.finishedAt ? moment(v.finishedAt).add(+9, 'h').format('MM/DD') + '終了' : '実施中'}</Week>
                    </TrainingListLabel>
                    <TrainingDate>
                      {DateFns.format(v.startedAt, 'MM月DD日')}
                    </TrainingDate>
                  </TrainingListLeft>
                  <TrainingListRight>
                    <MuscleRate>
                      <Icon.Muscle
                        size={`${Size.LARGE}px`}
                        color={Color.DARK_GRAY}
                      />
                      <StatusList>
                        {v.patientDayTrainingResistanceLogs &&
                          v.patientDayTrainingResistanceLogs.map(
                            (
                              resistanceLog: ResistanceLog,
                              resistanceLogIndex: number
                            ) => (
                              <StatusIcon
                                key={resistanceLogIndex}
                                evaluation={Number(resistanceLog.evaluation)}
                              />
                            )
                          )}
                      </StatusList>
                    </MuscleRate>
                    <HeartRate>
                      <Icon.HeartRate
                        size={`${Size.LARGE}px`}
                        color={Color.DARK_GRAY}
                      />
                      <StatusList>
                        {v.aerobicLog && (
                          <StatusIcon
                            evaluation={Number(v.aerobicLog.evaluation)}
                          />
                        )}
                      </StatusList>
                    </HeartRate>
                  </TrainingListRight>
                </TrainingListItem>
              ))}
          </TrainingListWrapper>
        </PageContents>
      )}
      {modal && (
        <Modal>
          <ModalInner>
            <CloseButton top={12} right={12} onClick={toggleModal} />
            <h2>共有設定</h2>
            <FormWrapper>
              <Form
                onSubmit={onSubmit}
                initialValues={{
                  enable: shared.enable,
                }}
                validate={(values: any) => {
                  const errors: any = {}
                  errors.password = composeValidators(
                    required('パスワードを入力してください')
                  )(values.password)
                  return errors
                }}
                render={({ handleSubmit, submitting, errors }) => (
                  <form onSubmit={handleSubmit}>
                    <FieldSet>
                      <InvalidMessage>{errors.enable}</InvalidMessage>
                      <Field name="enable" type="checkbox">
                        {({ input }) => (
                          <ToggleWrapper>
                            <ToggleLabel>共有を許可する</ToggleLabel>
                            <ToggleButton className="toggle" htmlFor="enable">
                              <div className={input.value ? 'on' : ''} />
                            </ToggleButton>
                            <input
                              type="checkbox"
                              id="enable"
                              {...input}
                              checked={input.value === true ? true : false}
                            />
                          </ToggleWrapper>
                        )}
                      </Field>
                    </FieldSet>
                    <FieldSet>
                      <URLWrapper>
                        <URLLabel>共有URL</URLLabel>
                        <URLLink
                          href={
                            'line://msg/text/?' +
                            siteUrl +
                            'share/' +
                            shared.key
                          }
                        >
                          LINEで共有
                        </URLLink>
                        <br />
                        <URLText>
                          {siteUrl}share/{shared.key}
                        </URLText>
                        <ShareButton
                          config={{
                            params: {
                              title: '運動履歴共有',
                              text: '共有用URL',
                              url: siteUrl + 'share/' + shared.key,
                            },
                            /* tslint:disable-next-line:no-console */
                            onShareSuccess: () => console.log('Success'),
                            /* tslint:disable-next-line:no-console */
                            onShareError: (error: Error) =>
                              console.log('error', error),
                          }}
                        />
                      </URLWrapper>
                    </FieldSet>
                    <FieldSet>
                      <TextFieldItem
                        name="password"
                        type="text"
                        label="共有URLで入力してもらうパスワードを入力"
                        placeholder="password"
                        vertical
                        required={true}
                      />
                    </FieldSet>
                    <URLText>
                      {shared.password
                        ? 'パスワード設定済みです。変更する場合は入力して保存してください。'
                        : 'パスワードを設定してください。'}
                    </URLText>
                    <ButtonGroup vertical css="width: 168px;margin: 30px auto;">
                      <Button primary>保存する</Button>
                    </ButtonGroup>
                  </form>
                )}
              />
            </FormWrapper>
          </ModalInner>
        </Modal>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div``
const PageContents = styled.div``
const UserDetail = styled.div`
  margin: 16px 8px;
  display: flex;
  align-items: center;
  position: relative;
`
const Modal = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index; 10;
`
const ModalInner = styled.div`
  max-width: ${Size.BREAK_POINT.MOBILE}px;
  position: relative;
  background: white;
  height: 100%;
  margin: 0 auto;
  padding: 16px 8px;
  h2 {
    font-size: ${Size.LARGE}px;
    font-weight: normal;
  }
`
const UserName = styled.div`
  font-size: ${Size.BASE}px;
  margin-left: 8px;
  margin-right: 8px;
`
const Age = styled.div`
  font-size: ${Size.BASE}px;
`
const Share = styled.div`
  font-size: ${Size.BASE}px;
  position: absolute;
  right: 0;
  button {
    padding: 8px 16px;
  }
`
const InvalidMessage = styled.p`
  color: ${Color.STATUS_COLOR.STATUS_1};
  margin-top: 10px;
  font-size: ${Size.FONT.SMALL}px;
`
const ToggleWrapper = styled.div`
  input {
    display: none;
  }
`
const ToggleLabel = styled.div`
  font-size: ${Size.FONT.XSMALL}px;
  font-weight: ${Size.FONT_WEIGHT.BOLD};
`
const ToggleButton = styled.label`
  div {
    width: 32px !important;
    border-radius: 4px;
    height: 10px;
    background-color: ${Color.VERY_LIGHT_GRAY_2};
    position: relative;
    margin: 20px 0;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -o-user-select: none;
    user-select: none;
    :after {
      border-radius: 100%;
      background-color: ${Color.VERY_LIGHT_GRAY};
      position: absolute;
      left: 0;
      top: -3px;
      width: 15px;
      height: 15px;
      content: '';
      -webkit-transform: translate(0);
      -ms-transform: translate(0);
      transform: translateX(0);
      transition: all 0.2s ease-in-out;
      -webkit-transition: all 0.2s ease-in-out;
      -moz-transition: all 0.2s ease-in-out;
    }
    :hover {
      cursor: pointer;
    }
  }
  .on {
    background-color: ${Color.STRONG_GREEN};
    transform: translate3d(0, 0, 0);
    :after {
      background-color: ${Color.STATUS_3};
      -webkit-transform: translate(17px);
      -ms-transform: translate(17px);
      transform: translateX(17px);
    }
  }
`
const FormWrapper = styled.div`
  padding: 28px 0;
`
const FieldSet = styled.div<{
  vertical?: boolean
}>`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  div {
    width: 100%;
  }
  ${when('vertical')(`
    flex-direction: column;
    align-items: start;
  `)};
`
const TrainingMetaWrapper = styled.div`
  display: flex;
  align-items: end;
  margin: 8px;
  justify-content: space-between;
`
const TrainingMetaItem = styled.div`
  display: flex;
  flex-direction: column;
`
const Lablel = styled.div`
  font-size: ${Size.BASE}px;
  color: ${Color.VERY_LIGHT_GRAY};
  margin-bottom: 8px;
  font-weight: ${Size.FONT_WEIGHT.NORMAL};
`
const TrainingMetaBody = styled.div`
  font-size: ${Size.LARGE}px;
  display: flex;
  align-items: baseline;
  font-weight: ${Size.FONT_WEIGHT.THIN};
`
/*
const Count = styled.div`
  font-size: ${Size.LARGE}px;
  margin: 0 4px;
  font-weight: ${Size.FONT_WEIGHT.BOLD};
`
*/
const TrainingGraphWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 8px;
`
const GraphContents = styled.div`
  border-bottom: solid 1px ${Color.VERY_LIGHT_GRAY};
  width: 100%;
  overflow-x: scroll;
  svg {
    .y_axis {
      path.domain {
        stroke: white;
      }
      line {
        stroke: ${Color.STATUS_COLOR.STATUS_BORDER};
      }
    }
  }
`
const TrainingListWrapper = styled.div`
  margin: 8px 0;
  display: flex;
  flex-direction: column;
`
const TrainingListItem = styled.div`
  display: flex;
  padding: 12px;
  background: ${Color.BG_GRADIENT_0};
  cursor: pointer;
`
const TrainingListLeft = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 8px;
  min-width: 95px;
`
const TrainingListRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
`
const TrainingListLabel = styled.div`
  font-size: ${Size.BASE}px;
  color: ${Color.VERY_LIGHT_GRAY};
  font-weight: ${Size.FONT_WEIGHT.NORMAL};
  display: flex;
  align-items: baseline;
  margin-bottom: 8px;
`
const TrainingDate = styled.div`
  font-size: ${Size.XLARGE}px;
  color: ${Color.BLACK};
  font-weight: ${Size.FONT_WEIGHT.THIN};
`
const TrainingCount = styled.div`
  font-size: ${Size.MIDDLE2}px;
  color: ${Color.VERY_LIGHT_GRAY};
`
const Week = styled.div`
  color: ${Color.VERY_LIGHT_GRAY};
  margin-left: 8px;
`

const MuscleRate = styled.div`
  display: flex;
`
const HeartRate = styled.div`
  display: flex;
`

const StatusList = styled.div`
  margin-left: 8px;
  span {
    margin-right: 4px;
  }
`

const URLLabel = styled.div`
  font-size: ${Size.FONT.XSMALL}px;
  font-weight: ${Size.FONT_WEIGHT.BOLD};
`
const URLText = styled.p`
  padding-top: 5px;
  font-size: ${Size.FONT.XSMALL}px;
`
const URLLink = styled.a`
  font-size: ${Size.FONT.XSMALL}px;
  font-weight: ${Size.FONT_WEIGHT.BOLD};
  margin: ;
`
const URLWrapper = styled.div`
  padding: 5px 0;
`



// WEBPACK FOOTER //
// ./src/components/pages/report/contents.tsx